import { useState } from "preact/hooks"
import { useDropzone } from "react-dropzone"
import { Button } from "@/components/ui/button"
import { toast } from "@/components/ui/use-toast"
import { FileText, Upload, X } from "lucide-react"

interface SkuMappingUploadResponse {
  success_count: number
  updated_count: number
  skipped_count: number
  error_count: number
  errors: string[]
}

const UPC_PRIORITY_COLUMNS = ["Product UPC", "UPC for lookup", "Case UPC / GTIN"]
const FALLBACK_KEY_COLUMNS = ["UNFI East", "UNFI West", "KeHE", "Rainforest"]
const REQUIRED_MAPPING_COLUMNS = ["Product Line", "Product Group"]

function getSelectedFileLabel(file: File | null): string {
  return file ? `${file.name} (${Math.round(file.size / 1024)} KB)` : "No CSV selected"
}

export function SkuMappingUpload() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [lastResult, setLastResult] = useState<SkuMappingUploadResponse | null>(null)

  function handleDrop(files: File[]) {
    const file = files[0] || null
    setSelectedFile(file)
  }

  function handleClearSelectedFile() {
    setSelectedFile(null)
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: {
      "text/csv": [".csv"],
    },
  })

  function getUploadSummary(result: SkuMappingUploadResponse): string {
    return `Created ${result.success_count}, updated ${result.updated_count}, skipped ${result.skipped_count}, errors ${result.error_count}`
  }

  async function handleUpload() {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Choose a CSV file to upload.",
        variant: "destructive",
      })
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append("file", selectedFile)

      const response = await fetch("/api/organization/sku-mapping/upload", {
        method: "POST",
        credentials: "same-origin",
        body: formData,
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(errorText || "Failed to upload SKU mapping CSV")
      }

      const payload = await response.json()
      const result: SkuMappingUploadResponse = payload.data || payload
      setLastResult(result)
      toast({
        title: "SKU mapping upload complete",
        description: getUploadSummary(result),
        variant: result.error_count > 0 ? "destructive" : "default",
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to upload SKU mapping CSV"
      toast({
        title: "Upload failed",
        description: message,
        variant: "destructive",
      })
    } finally {
      setUploading(false)
    }
  }

  return (
    <div class="space-y-4">
      <div class="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        <p class="font-medium mb-2">Recommended CSV columns</p>
        <p class="mb-1">UPC lookup priority (first match wins):</p>
        <p class="text-xs mb-2">{UPC_PRIORITY_COLUMNS.join(" → ")}</p>
        <p class="mb-1">Additional fallback key columns:</p>
        <p class="text-xs mb-2">{FALLBACK_KEY_COLUMNS.join(", ")}</p>
        <p class="mb-1">Required mapping output columns:</p>
        <p class="text-xs">{REQUIRED_MAPPING_COLUMNS.join(", ")}</p>
      </div>

      <div
        {...(getRootProps() as object)}
        class={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
          isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="mx-auto h-10 w-10 text-gray-400 mb-3" />
        <p class="text-sm font-medium text-gray-900 mb-1">
          {isDragActive ? "Drop CSV here" : "Drag and drop SKU mapping CSV"}
        </p>
        <p class="text-xs text-gray-500 mb-3">Only `.csv` files are supported</p>
        <Button variant="outline" onClick={open} disabled={uploading}>
          Select CSV
        </Button>
      </div>

      <div class="flex items-center justify-between rounded-md border border-gray-200 p-3">
        <div class="flex items-center gap-2 text-sm">
          <FileText className="h-4 w-4 text-gray-500" />
          <span>{getSelectedFileLabel(selectedFile)}</span>
        </div>
        <div class="flex items-center gap-2">
          <Button variant="outline" onClick={handleClearSelectedFile} disabled={!selectedFile || uploading}>
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
          <Button onClick={handleUpload} disabled={!selectedFile || uploading}>
            {uploading ? "Uploading..." : "Upload SKU Mapping CSV"}
          </Button>
        </div>
      </div>

      {lastResult && (
        <div class="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
          <div>Created: {lastResult.success_count}</div>
          <div>Updated: {lastResult.updated_count}</div>
          <div>Skipped: {lastResult.skipped_count}</div>
          <div>Errors: {lastResult.error_count}</div>
          {lastResult.errors.length > 0 && (
            <div class="mt-2 max-h-32 overflow-y-auto text-xs text-red-700">
              {lastResult.errors.slice(0, 10).map((err: string, idx: number) => (
                <div key={`${idx}-${err}`}>{err}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
